import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom'; // Import useLocation for getting the current path

import localizationConstants from 'shared/util/translation/constants';
import { createAction } from 'shared/util/utility';

import * as LoginActions from 'features/login/store/login.action';
import { useSelector } from 'react-redux';
import { State } from 'shared/interface';
import * as actionTypes from 'store/action-types';
import { isEmpty } from 'lodash';

export const useInactivityTimer = (timeout = 1800000) => {
	const { t } = useTranslation();
	const timerRef = useRef<ReturnType<typeof setTimeout>>();
	const dispatch = useDispatch();
	const location = useLocation(); // Get the current path
	const { userData } = useSelector((state: State) => state.auth);
	const safeAdmin = Array.isArray(userData?.admin) ? userData.admin : [];

	const onLogout = useCallback(() => {
		LoginActions.logout(dispatch);
		dispatch(
			createAction(actionTypes.UPDATE_SITE_CONFIG, {
				sessionExpireMessage: t(localizationConstants.inActivityMessage)
			})
		);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
	}, [dispatch, t]);

	const resetTimer = useCallback(() => {
		// If user is on the login page or is NOT an admin/superadmin, handle the timer
		if (location.pathname === '/login') return;

		// Clear any existing timer
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		// Only set inactivity timer for regular users (non-admin)
		if (isEmpty(safeAdmin)) {
			timerRef.current = setTimeout(onLogout, timeout);
		}
	}, [location.pathname, onLogout, safeAdmin, timeout]);

	useEffect(() => {
		resetTimer();
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [resetTimer]);

	return resetTimer;
};
